// UPLOAD-ANYWHERE-1: turn a drop or a folder picker into real files.
// A folder is walked. It is never uploaded as a file of its own.
// Junk, unsupported types, files over 50 MB, and files past the 500 file
// cap are returned as skipped, each with a reason.

export const MAX_UPLOAD_FILES = 500;
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

const ACCEPT_EXTENSIONS = new Set([
  "pdf", "eml", "doc", "docx", "xls", "xlsx", "dwg", "dxf", "txt", "csv",
]);

const JUNK_NAMES = new Set([".ds_store", "thumbs.db", "desktop.ini"]);

export function fileDisplayName(file) {
  const relative = file && typeof file.webkitRelativePath === "string"
    ? file.webkitRelativePath
    : "";
  const segments = relative.split("/").filter(Boolean);
  if (segments.length) return segments[segments.length - 1];
  if (file && file.name) return file.name;
  return "file";
}

function extensionOf(name) {
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot === name.length - 1) return "";
  return name.slice(dot + 1).toLowerCase();
}

export function isJunkFile(file) {
  if (!file || !file.size) return true;
  const name = fileDisplayName(file).toLowerCase();
  if (JUNK_NAMES.has(name)) return true;
  if (name.startsWith("._")) return true;
  return false;
}

export function isAcceptedUpload(file) {
  const type = String((file && file.type) || "").toLowerCase();
  if (type.startsWith("image/")) return true;
  return ACCEPT_EXTENSIONS.has(extensionOf(fileDisplayName(file)));
}

export function partitionUploadFiles(files, alreadyQueued) {
  const accepted = [];
  const skipped = [];
  let room = MAX_UPLOAD_FILES - (alreadyQueued || 0);
  if (room < 0) room = 0;
  const list = Array.isArray(files) ? files : Array.from(files || []);
  for (let i = 0; i < list.length; i += 1) {
    const file = list[i];
    const name = fileDisplayName(file);
    if (isJunkFile(file)) {
      skipped.push({ name, reason: "junk file", file });
      continue;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      skipped.push({ name, reason: "over 50 MB", file });
      continue;
    }
    if (!isAcceptedUpload(file)) {
      skipped.push({ name, reason: "unsupported type", file });
      continue;
    }
    if (room <= 0) {
      skipped.push({ name, reason: `over the ${MAX_UPLOAD_FILES} file limit`, file });
      continue;
    }
    room -= 1;
    accepted.push(file);
  }
  return { accepted, skipped };
}

function readAllEntries(reader) {
  return new Promise((resolve, reject) => {
    const all = [];
    const next = () => {
      reader.readEntries((batch) => {
        if (!batch || batch.length === 0) {
          resolve(all);
          return;
        }
        for (let i = 0; i < batch.length; i += 1) all.push(batch[i]);
        next();
      }, reject);
    };
    next();
  });
}

function fileFromEntry(entry) {
  return new Promise((resolve, reject) => {
    entry.file(resolve, reject);
  });
}

async function filesFromEntry(entry) {
  if (!entry) return [];
  if (entry.isFile) {
    const file = await fileFromEntry(entry);
    return file ? [file] : [];
  }
  if (!entry.isDirectory || typeof entry.createReader !== "function") return [];
  const children = await readAllEntries(entry.createReader());
  const files = [];
  for (let i = 0; i < children.length; i += 1) {
    const nested = await filesFromEntry(children[i]);
    for (let j = 0; j < nested.length; j += 1) files.push(nested[j]);
  }
  return files;
}

export async function collectDroppedFiles(dataTransfer) {
  const items = dataTransfer && dataTransfer.items ? Array.from(dataTransfer.items) : [];
  const canWalk = items.some((item) => item && typeof item.webkitGetAsEntry === "function");
  if (!canWalk) {
    return Array.from((dataTransfer && dataTransfer.files) || []);
  }
  const files = [];
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (item.kind && item.kind !== "file") continue;
    const entry = item.webkitGetAsEntry();
    if (entry) {
      const walked = await filesFromEntry(entry);
      for (let j = 0; j < walked.length; j += 1) files.push(walked[j]);
      continue;
    }
    if (typeof item.getAsFile === "function") {
      const file = item.getAsFile();
      if (file) files.push(file);
    }
  }
  return files;
}
